/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { getVisibleRegionLabel } from '@theme/utilities';

/**
 * Custom element wrapping the "Notify me" button rendered in place of the
 * Add-to-cart button when the current variant is sold out.
 *
 * On click, opens the global <klaviyo-bis-dialog> — primed directly if a
 * variant is already known, otherwise (`data-needs-size-selection`) on its
 * size-picker step, using the grid pre-rendered into this component's
 * `<template>`. No-op if BIS is disabled (no dialog on page).
 */
class NotifyMeComponent extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.notify-me-button');
    this.button?.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#handleClick);
  }

  /**
   * The region-aware size label (EU/UK/US) for the currently selected size,
   * read live from the PDP's size picker — `data-variant-title` is a
   * server-rendered fallback that can't see the shopper's size-region
   * toggle (a sessionStorage preference, see variant-picker.js).
   * @returns {string}
   */
  #getVariantTitle() {
    const fallback = this.dataset.variantTitle || '';
    const productId = this.dataset.productId;
    if (!productId) return fallback;

    const section = this.closest('.shopify-section');
    const picker = section?.querySelector(`variant-picker[data-product-id="${productId}"]`);
    const fieldset = picker?.querySelector('fieldset.variant-option--grid[data-current-region]');
    const checked = fieldset?.querySelector('input:checked');
    const label = checked?.closest('label');

    return getVisibleRegionLabel(label, fallback);
  }

  /**
   * A size already picked in the enclosing form (the Quick View picks sizes
   * client-side, so the server-rendered flag can't know about it).
   * @returns {{ id: string, title: string } | null}
   */
  #pickedVariantInForm() {
    const form = this.closest('form');
    const checked = form?.querySelector('fieldset.variant-option--grid input:checked');
    const idInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]') ?? null);
    if (!(checked instanceof HTMLInputElement) || !idInput?.value) return null;
    return { id: idInput.value, title: getVisibleRegionLabel(checked.closest('label'), checked.value || '') };
  }

  #handleClick = () => {
    const dialog = /** @type {any} */ (document.getElementById('klaviyo-bis-dialog'));
    if (!dialog) return;

    if (this.dataset.needsSizeSelection === 'true') {
      const picked = this.#pickedVariantInForm();
      if (picked && dialog.openForVariant) {
        dialog.openForVariant({
          variantId: picked.id,
          productTitle: this.dataset.productTitle,
          variantTitle: picked.title,
        });
        return;
      }
      if (!dialog.openForSizeSelection) return;
      const template = this.querySelector('template[data-notify-size-grid]');
      dialog.openForSizeSelection({
        productTitle: this.dataset.productTitle,
        sizeGrid: template instanceof HTMLTemplateElement ? template.content.cloneNode(true) : null,
      });
      return;
    }

    if (!dialog.openForVariant) return;
    dialog.openForVariant({
      variantId: this.dataset.variantId,
      productTitle: this.dataset.productTitle,
      variantTitle: this.#getVariantTitle(),
    });
  };
}

if (!customElements.get('notify-me-component')) {
  customElements.define('notify-me-component', NotifyMeComponent);
}
